import React, { useState, useEffect } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import { useNavigate } from "react-router-dom";
import {
  getReportsDashboard,
  getPayrollDashboardKPIs,
} from "../../../../services/workspace/reports.service";
import { toast } from "react-hot-toast";
import DefaultButton from "../../../components/buttons/defaultButton";
import DefaultSearchSelectInput from "../../../components/inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../../../components/inputs/defaultDatePicker";
import {
  FiDollarSign,
  FiUsers,
  FiClock,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiFileText,
  FiDownload,
  FiArrowRight,
} from "react-icons/fi";
import moment from "moment";
import { getDepartmentList } from "../../../../services/workspace/department.service";

const items = [{ label: "Reports & Analytics Dashboard", path: "" }];

const ReportsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [departments, setDepartments] = useState([]);

  // Filters
  const [dateRange, setDateRange] = useState({
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  });
  // Temporary state for date inputs to prevent refresh on month navigation
  const [tempDateRange, setTempDateRange] = useState({
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const [selectedDepartment, setSelectedDepartment] = useState("");

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Sync tempDateRange with dateRange when dateRange changes externally
  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedDepartment]);

  const fetchDepartments = async () => {
    try {
      const response = await getDepartmentList();
      // Extract departments array from response.department.data
      const departments = response?.department?.data || [];
      const deptOptions = [
        { value: "", label: "All Departments" },
        ...departments.map((dept) => ({
          value: dept.id,
          label: dept.name,
        })),
      ];
      setDepartments(deptOptions);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to fetch departments:", error);
      }
      // Don't show error toast, just set default empty list
      setDepartments([{ value: "", label: "All Departments" }]);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Try new Reports Dashboard API first
      try {
        const response = await getReportsDashboard({
          date_start: dateRange.start,
          date_end: dateRange.end,
          department_id: selectedDepartment || undefined,
        });
        setDashboard(response.data || response);
      } catch (reportsError) {
        // Check if it's a 404 or the endpoint doesn't exist
        const is404 = reportsError?.response?.status === 404 ||
                     reportsError?.response?.data?.statusCode === 404 ||
                     reportsError?.statusCode === 404 ||
                     reportsError?.message?.includes("404") ||
                     reportsError?.message?.includes("not found") ||
                     reportsError?.response?.data?.error === "NotFoundHttpException";

        if (process.env.NODE_ENV === 'development') {
        }

        // Silently fallback to payroll API if 404, don't show error
        if (!is404) {
          // If it's not a 404, log it but still try fallback
          if (process.env.NODE_ENV === 'development') {
            console.warn("Reports Dashboard API error (non-404):", reportsError);
          }
        }

        // Fallback: Use existing Payroll Dashboard API
        try {
          const payrollResponse = await getPayrollDashboardKPIs({
            start_date: dateRange.start,
            end_date: dateRange.end,
            department_id: selectedDepartment || undefined,
          });

          // Transform payroll data to dashboard format
          const transformedData = {
            payroll_cost: {
              total: payrollResponse?.data?.total_payroll_cost || payrollResponse?.total_payroll_cost || 0,
              change_percentage: payrollResponse?.data?.payroll_change_percentage || payrollResponse?.payroll_change_percentage || 0,
            },
            headcount: {
              active: payrollResponse?.data?.active_employees || payrollResponse?.active_employees || 0,
              new_joiners: payrollResponse?.data?.new_joiners || payrollResponse?.new_joiners || 0,
              exits: payrollResponse?.data?.exits || payrollResponse?.exits || 0,
            },
            attendance: {
              attendance_rate: payrollResponse?.data?.attendance_rate || payrollResponse?.attendance_rate || 0,
              late_count: payrollResponse?.data?.late_count || payrollResponse?.late_count || 0,
              absent_count: payrollResponse?.data?.absent_count || payrollResponse?.absent_count || 0,
            },
            leave: {
              total_applications: payrollResponse?.data?.leave_applications || payrollResponse?.leave_applications || 0,
              pending: payrollResponse?.data?.pending_leaves || payrollResponse?.pending_leaves || 0,
              approved: payrollResponse?.data?.approved_leaves || payrollResponse?.approved_leaves || 0,
            },
            reimbursement: {
              total_spend: payrollResponse?.data?.reimbursement_spend || payrollResponse?.reimbursement_spend || 0,
              change_percentage: payrollResponse?.data?.reimbursement_change || payrollResponse?.reimbursement_change || 0,
            },
            attrition: {
              rate: payrollResponse?.data?.attrition_rate || payrollResponse?.attrition_rate || 0,
              period: "Last 12 months",
            },
          };

          setDashboard(transformedData);
        } catch (payrollError) {
          // Both APIs failed, show error and set default data
          if (process.env.NODE_ENV === 'development') {
            console.error("Both dashboard APIs failed:", { reportsError, payrollError });
          }
          toast.error("Failed to fetch dashboard data");
          // Set default empty data
          setDashboard({
            payroll_cost: { total: 0, change_percentage: 0 },
            headcount: { active: 0, new_joiners: 0, exits: 0 },
            attendance: { attendance_rate: 0, late_count: 0, absent_count: 0 },
            leave: { total_applications: 0, pending: 0, approved: 0 },
            reimbursement: { total_spend: 0, change_percentage: 0 },
            attrition: { rate: 0, period: "Last 12 months" },
          });
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Unexpected error fetching dashboard data:", error);
      }
      toast.error("Failed to fetch dashboard data");
      // Set default empty data
      setDashboard({
        payroll_cost: { total: 0, change_percentage: 0 },
        headcount: { active: 0, new_joiners: 0, exits: 0 },
        attendance: { attendance_rate: 0, late_count: 0, absent_count: 0 },
        leave: { total_applications: 0, pending: 0, approved: 0 },
        reimbursement: { total_spend: 0, change_percentage: 0 },
        attrition: { rate: 0, period: "Last 12 months" },
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <PageTableHeader breadcrumbs={items} />
        {/* Skeleton Filters */}
        <div className="bg-white rounded-xl border border-borderColor p-6 animate-pulse">
          <div className="h-5 w-16 bg-gray-200 rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
        {/* Skeleton KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "border-l-green-500",
            "border-l-blue-500",
            "border-l-purple-500",
            "border-l-orange-500",
            "border-l-indigo-500",
            "border-l-red-500",
          ].map((accent, i) => (
            <div
              key={i}
              className={`bg-white p-5 rounded-xl border border-borderColor border-l-[3px] ${accent} animate-pulse`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="h-3 w-28 bg-gray-200 rounded" />
                  <div className="h-8 w-24 bg-gray-200 rounded" />
                  <div className="h-3 w-20 bg-gray-100 rounded" />
                </div>
                <div className="w-12 h-12 rounded-full bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
        {/* Skeleton Report Categories */}
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-gray-200 rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white p-5 rounded-xl border border-borderColor">
                <div className="w-10 h-10 rounded-full bg-gray-100 mb-3" />
                <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-full bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: "Total Payroll Cost",
      value: `₹${dashboard?.payroll_cost?.total?.toLocaleString() || 0}`,
      icon: <FiDollarSign className="text-xl text-green-600" />,
      bgColor: "bg-green-50",
      ringColor: "ring-green-100",
      accentColor: "border-l-green-500",
      change: dashboard?.payroll_cost?.change_percentage || 0,
      link: "/reports/payroll-cost",
    },
    {
      title: "Active Employees",
      value: dashboard?.headcount?.active || 0,
      icon: <FiUsers className="text-xl text-blue-600" />,
      bgColor: "bg-blue-50",
      ringColor: "ring-blue-100",
      accentColor: "border-l-blue-500",
      subtext: `New: ${dashboard?.headcount?.new_joiners || 0} | Exit: ${dashboard?.headcount?.exits || 0}`,
      link: "/reports/headcount-attrition",
    },
    {
      title: "Attendance Rate",
      value: `${dashboard?.attendance?.attendance_rate || 0}%`,
      icon: <FiClock className="text-xl text-purple-600" />,
      bgColor: "bg-purple-50",
      ringColor: "ring-purple-100",
      accentColor: "border-l-purple-500",
      subtext: `Late: ${dashboard?.attendance?.late_count || 0} | Absent: ${dashboard?.attendance?.absent_count || 0}`,
      link: "/reports/attendance-summary",
    },
    {
      title: "Leave Applications",
      value: dashboard?.leave?.total_applications || 0,
      icon: <FiCalendar className="text-xl text-orange-600" />,
      bgColor: "bg-orange-50",
      ringColor: "ring-orange-100",
      accentColor: "border-l-orange-500",
      subtext: `Pending: ${dashboard?.leave?.pending || 0} | Approved: ${dashboard?.leave?.approved || 0}`,
      link: "/reports/leave-trends",
    },
    {
      title: "Reimbursement Spend",
      value: `₹${dashboard?.reimbursement?.total_spend?.toLocaleString() || 0}`,
      icon: <FiFileText className="text-xl text-indigo-600" />,
      bgColor: "bg-indigo-50",
      ringColor: "ring-indigo-100",
      accentColor: "border-l-indigo-500",
      change: dashboard?.reimbursement?.change_percentage || 0,
      subtext: `Approved: ₹${(dashboard?.reimbursement?.approved_spend || 0).toLocaleString()} | Rejected: ₹${(dashboard?.reimbursement?.rejected_spend || 0).toLocaleString()}`,
      link: "/reports/reimbursement-spend",
    },
    {
      title: "Attrition Rate",
      value: `${dashboard?.attrition?.rate || 0}%`,
      icon: <FiTrendingDown className="text-xl text-red-600" />,
      bgColor: "bg-red-50",
      ringColor: "ring-red-100",
      accentColor: "border-l-red-500",
      subtext: dashboard?.attrition?.period || "Last 12 months",
      link: "/reports/headcount-attrition",
    },
  ];

  const reportCategories = [
    {
      title: "Payroll Reports",
      icon: <FiDollarSign className="text-lg text-green-600" />,
      iconBg: "bg-green-50",
      description: "Payroll cost analysis by department, trends, and comparisons",
      link: "/reports/payroll-cost",
    },
    {
      title: "Attendance Reports",
      icon: <FiClock className="text-lg text-purple-600" />,
      iconBg: "bg-purple-50",
      description: "Attendance summary, late/absent analysis, overtime reports",
      link: "/reports/attendance-summary",
    },
    {
      title: "Leave Reports",
      icon: <FiCalendar className="text-lg text-orange-600" />,
      iconBg: "bg-orange-50",
      description: "Leave trends, balance, encashment liabilities",
      link: "/reports/leave-trends",
    },
    {
      title: "Reimbursement Reports",
      icon: <FiFileText className="text-lg text-indigo-600" />,
      iconBg: "bg-indigo-50",
      description: "Reimbursement spend by category and department",
      link: "/reports/reimbursement-spend",
    },
    {
      title: "Headcount & Attrition",
      icon: <FiUsers className="text-lg text-blue-600" />,
      iconBg: "bg-blue-50",
      description: "Employee headcount, attrition analysis, new joiners",
      link: "/reports/headcount-attrition",
    },
  ];

  // const quickActions = [
  //   {
  //     title: "View Export History",
  //     description: "Download previously exported reports",
  //     icon: <FiDownload className="text-xl text-indigo-600" />,
  //     iconBg: "bg-indigo-50",
  //     onClick: () => navigate("/reports/export-history"),
  //   },
  //   {
  //     title: "Schedule Reports",
  //     description: "Set up automated report delivery",
  //     icon: <FiCalendar className="text-xl text-orange-600" />,
  //     iconBg: "bg-orange-50",
  //     onClick: () => navigate("/reports/schedule"),
  //   },
  // ];

  return (
    <div className="flex flex-col gap-6">
      <PageTableHeader breadcrumbs={items} />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-borderColor p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DefaultDatePicker
            label="Start Date"
            name="start_date"
            value={tempDateRange.start}
            maxDate={moment().toDate()}
            onChange={(date) => {
              setTempDateRange({ ...tempDateRange, start: date });
            }}
            onBlur={() => {
              if (tempDateRange.start !== dateRange.start) {
                setDateRange({ ...dateRange, start: tempDateRange.start });
              }
            }}
            placeholder="Select Start Date"
          />
          <DefaultDatePicker
            label="End Date"
            name="end_date"
            value={tempDateRange.end}
            maxDate={moment().toDate()}
            onChange={(date) => {
              setTempDateRange({ ...tempDateRange, end: date });
            }}
            onBlur={() => {
              if (tempDateRange.end !== dateRange.end) {
                setDateRange({ ...dateRange, end: tempDateRange.end });
              }
            }}
            placeholder="Select End Date"
          />
          <div>
            <DefaultSearchSelectInput
              options={departments}
              label="Department"
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              placeholder="Select department"
              required
            />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className={`bg-white rounded-xl border border-borderColor border-l-[3px] ${card.accentColor} p-5 hover:shadow-md transition-all cursor-pointer group`}
            onClick={() => navigate(card.link)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {card.value}
                </p>
                {card.subtext && (
                  <p className="text-xs text-gray-500">{card.subtext}</p>
                )}
                {card.change !== undefined && Number(card.change) !== 0 && (
                  <span
                    className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      card.change >= 0
                        ? "bg-green-50 text-green-600"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    {card.change >= 0 ? (
                      <FiTrendingUp className="text-[10px]" />
                    ) : (
                      <FiTrendingDown className="text-[10px]" />
                    )}
                    {card.change >= 0 ? "+" : ""}
                    {card.change}% from last period
                  </span>
                )}
              </div>
              <div
                className={`w-12 h-12 rounded-full ${card.bgColor} ring-4 ${card.ringColor} flex items-center justify-center`}
              >
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Report Categories */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Report Categories
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportCategories.map((category, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-borderColor p-5 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md group"
              onClick={() => navigate(category.link)}
            >
              <div className={`w-10 h-10 rounded-full ${category.iconBg} flex items-center justify-center mb-3`}>
                {category.icon}
              </div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-sm text-gray-900">
                  {category.title}
                </h3>
                <FiArrowRight className="text-gray-300 text-sm group-hover:text-gray-500 transition-colors" />
              </div>
              <p className="text-xs text-gray-500">{category.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      {/* <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          {quickActions.map((action, index) => (
            <div
              key={index}
              onClick={action.onClick}
              className="bg-white rounded-xl border border-borderColor p-5 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div
                className={`w-11 h-11 rounded-full ${action.iconBg} flex items-center justify-center mb-3`}
              >
                {action.icon}
              </div>
              <p className="font-semibold text-sm text-gray-900">
                {action.title}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {action.description}
              </p>
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default ReportsDashboard;
